/**
 * BranchPickupManager — non-blocking global floating pill notification system
 * for cross-branch pickup orders.
 *
 * Design spec: /app/memory/BRANCH_PICKUP_DESIGN.md — §7 (Frontend Pills Pattern)
 * Phase B — Frontend Pills + Dialogs (Feb 2026)
 *
 * Architecture:
 * - Amber pill (bottom-right, above PriceScanManager): serves outgoing orders
 *   → shows count of active outgoing orders for Sale branch (Branch A)
 *   → shows OTP code when status=otp_pending
 *   → shows diff view when status=modified
 * - Indigo pill (bottom-right, below amber): incoming orders for Serving branch (Branch B)
 *   → shows count of pending confirmations
 *   → opens serving-branch dialog to confirm/modify each order
 *
 * Polling: 15 seconds (or 5s immediately after a state change)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GitMerge, X, ChevronDown, ChevronUp, AlertCircle, Check, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import PrintEngine from '../lib/PrintEngine';
import SendToPrintModal from './SendToPrintModal';

// Print the branch-pickup receipt after finalize.
// Non-BIR branch → Order Slip / Trust Receipt. BIR branch (auto) → Official
// Sales/Charge Invoice (already issued server-side on the payment branch series).
async function printPickupReceipt(invoiceId) {
  try {
    const [invRes, bizRes] = await Promise.all([
      api.get(`/invoices/${invoiceId}`),
      api.get('/settings/business-info').catch(() => ({ data: {} })),
    ]);
    const inv = invRes.data || {};
    let docCode = '';
    try {
      const r = await api.post('/doc/generate-code', { doc_type: 'invoice', doc_id: inv.id });
      docCode = r.data?.code || '';
    } catch { /* print without QR */ }
    PrintEngine.print({
      type: PrintEngine.getDocType(inv) || 'order_slip',
      data: inv,
      format: 'full_page',
      businessInfo: bizRes.data || {},
      docCode,
    });
  } catch (e) {
    toast.error('Receipt print failed: ' + (e?.message || ''));
  }
}

const POLL_INTERVAL_MS = 15_000;
const QUICK_POLL_MS   = 5_000;

// ── Status badge colours ────────────────────────────────────────────────────
function statusColor(status) {
  const map = {
    pending_confirmation: 'bg-yellow-100 text-yellow-800',
    confirmed:            'bg-blue-100 text-blue-800',
    modified:             'bg-orange-100 text-orange-800',
    otp_pending:          'bg-emerald-100 text-emerald-800',
    finalized:            'bg-green-100 text-green-800',
    cancelled:            'bg-red-100 text-red-800',
    expired:              'bg-gray-100 text-gray-700',
  };
  return map[status] || 'bg-gray-100 text-gray-600';
}

function statusLabel(status) {
  const map = {
    pending_confirmation: 'Awaiting',
    confirmed:            'Confirmed',
    modified:             'Changes Proposed',
    otp_pending:          'OTP Ready',
    finalized:            'Done',
    cancelled:            'Cancelled',
    expired:              'Expired',
  };
  return map[status] || status;
}

// ── Serving branch dialog (Branch B side) ──────────────────────────────────
function IncomingOrderDialog({ order, onClose, onRefresh }) {
  const { effectiveBranchId } = useAuth();
  const [items, setItems]   = useState(() =>
    (order.items || []).map(i => ({
      ...i,
      qty_confirmed:        i.qty_confirmed        ?? i.qty_requested,
      unit_price_confirmed: i.unit_price_confirmed ?? i.unit_price_requested,
    }))
  );
  const [note, setNote]       = useState('');
  const [loading, setLoading] = useState(false);

  const total = items.reduce((s, i) => s + (parseFloat(i.qty_confirmed || 0) * parseFloat(i.unit_price_confirmed || 0)), 0);
  const isOtpReady = order.status === 'otp_pending';

  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await api.post(`/branch-pickups/${order.id}/confirm`, {
        branch_id: effectiveBranchId,
        items: items.map(i => ({
          product_id:           i.product_id,
          qty_confirmed:        parseFloat(i.qty_confirmed || 0),
          unit_price_confirmed: parseFloat(i.unit_price_confirmed || 0),
        })),
        note,
      });
      toast.success('Pickup order confirmed');
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to confirm order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this pickup order?')) return;
    setLoading(true);
    try {
      await api.post(`/branch-pickups/${order.id}/cancel`, { reason: 'Declined by serving branch' });
      toast.success('Order cancelled');
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="incoming-order-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-indigo-50 border-b border-indigo-100">
          <div>
            <h2 className="text-base font-semibold text-indigo-900" data-testid="incoming-dialog-title">
              Incoming Pickup — {order.pickup_number}
            </h2>
            <p className="text-xs text-indigo-600 mt-0.5">
              Requested by: <strong>{order.sale_branch_name}</strong> · Customer: {order.customer_name || 'Walk-in'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-indigo-100 transition-colors" data-testid="close-incoming-dialog">
            <X size={18} className="text-indigo-700" />
          </button>
        </div>

        {/* OTP display — serving branch reads this code to the sale branch,
            who enters it on their side to finalize the pickup. */}
        {isOtpReady && (
          <div className="px-5 py-5 bg-emerald-50 border-b border-emerald-100 text-center" data-testid="incoming-otp-panel">
            <p className="text-xs font-semibold text-emerald-800 mb-2">
              Confirmed — give this code to <strong>{order.sale_branch_name}</strong> to complete the sale:
            </p>
            <div className="text-4xl font-mono font-bold tracking-[0.4em] text-emerald-700 select-all" data-testid="incoming-otp-code">
              {order.otp_code || '••••••'}
            </div>
            <p className="text-[11px] text-emerald-600 mt-2">
              They enter it on their screen to finalize. Stock is reserved until then.
            </p>
            <button
              onClick={async () => {
                // D9 (BT Batch 2 P5): a lost/expired code no longer strands
                // the pickup — mint a fresh one in place.
                try {
                  const res = await api.post(`/branch-pickups/${order.id}/generate-otp`);
                  if (res.data?.otp_code) { toast.success('New code generated'); onRefresh?.(); }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Could not generate a new code');
                }
              }}
              className="mt-2 text-[11px] px-3 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              data-testid={`pickup-resend-otp-${order.id}`}>
              Generate new code
            </button>
          </div>
        )}

        {/* Items table */}
        <div className="overflow-x-auto max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Product</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Requested</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Available</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Qty to Serve</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Unit Price</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item, idx) => (
                <tr key={item.product_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-800">{item.product_name}</div>
                    <div className="text-xs text-gray-400">{item.sku}</div>
                  </td>
                  <td className="text-right px-3 py-2 text-gray-600">{item.qty_requested} {item.unit}</td>
                  <td className="text-right px-3 py-2">
                    <span className={`text-xs font-medium ${parseFloat(item.stock_at_request || 0) >= parseFloat(item.qty_requested || 0) ? 'text-emerald-600' : 'text-red-600'}`}>
                      {item.stock_at_request ?? '?'}
                    </span>
                  </td>
                  <td className="text-right px-3 py-2">
                    <input
                      type="number" min="0" step="0.001"
                      value={item.qty_confirmed}
                      onChange={e => updateItem(idx, 'qty_confirmed', e.target.value)}
                      className="w-20 text-right border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-indigo-400"
                      data-testid={`qty-confirmed-${idx}`}
                    />
                  </td>
                  <td className="text-right px-3 py-2">
                    <input
                      type="number" min="0" step="0.001"
                      value={item.unit_price_confirmed}
                      onChange={e => updateItem(idx, 'unit_price_confirmed', e.target.value)}
                      className="w-24 text-right border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-indigo-400"
                      data-testid={`price-confirmed-${idx}`}
                    />
                  </td>
                  <td className="text-right px-3 py-2 font-medium text-gray-800">
                    {(parseFloat(item.qty_confirmed || 0) * parseFloat(item.unit_price_confirmed || 0)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Note + total — only while awaiting confirmation */}
        {!isOtpReady && (
        <div className="px-5 py-3 border-t bg-gray-50 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Modification note (optional)</span>
            <span className="font-semibold text-gray-800">Total: {total.toFixed(2)}</span>
          </div>
          <input
            type="text"
            placeholder="Note for Branch A (optional)…"
            value={note}
            onChange={e => setNote(e.target.value)}
            className="border border-gray-200 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:border-indigo-400"
            data-testid="modification-note"
          />
        </div>
        )}

        {/* Actions */}
        {isOtpReady ? (
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
              data-testid="btn-cancel-pickup"
            >
              Cancel Order
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              data-testid="btn-close-otp"
            >
              Done
            </button>
          </div>
        ) : (
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            data-testid="btn-decline-pickup"
          >
            Decline
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            data-testid="btn-confirm-pickup"
          >
            {loading ? 'Processing…' : 'Confirm & Serve'}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

// ── Outgoing order dialog (Branch A side — shows status, OTP, diff) ─────────
function OutgoingOrderDialog({ orders, onClose, onRefresh, onRemotePrint }) {
  const { effectiveBranchId } = useAuth();
  const [otpInputs, setOtpInputs]   = useState({});
  const [loading, setLoading]       = useState({});

  const handleVerifyOtp = async (order) => {
    const otp = (otpInputs[order.id] || '').trim().toUpperCase();
    if (!otp) { toast.error('Enter the OTP code'); return; }
    setLoading(prev => ({ ...prev, [order.id]: true }));
    try {
      const res = await api.post(`/branch-pickups/${order.id}/verify-otp`, { otp_code: otp, branch_id: effectiveBranchId });
      const invoiceId = res.data?.invoice_id;
      // This dialog closes on finalize, so remote print rides on the toast.
      toast.success(`Pickup finalized! Invoice ${res.data.invoice_number}`, invoiceId && onRemotePrint ? {
        duration: 10_000,
        action: { label: 'Send to Branch Printer', onClick: () => onRemotePrint(invoiceId) },
      } : undefined);
      if (invoiceId) {
        printPickupReceipt(invoiceId);
      }
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(prev => ({ ...prev, [order.id]: false }));
    }
  };

  const handleAcceptChanges = async (order) => {
    setLoading(prev => ({ ...prev, [order.id]: true }));
    try {
      await api.post(`/branch-pickups/${order.id}/accept-changes`, { branch_id: effectiveBranchId });
      toast.success('Changes accepted — OTP will be generated');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setLoading(prev => ({ ...prev, [order.id]: false }));
    }
  };

  const handleCancel = async (order) => {
    if (!window.confirm('Cancel this pickup order?')) return;
    setLoading(prev => ({ ...prev, [order.id]: true }));
    try {
      await api.post(`/branch-pickups/${order.id}/cancel`, { reason: 'Cancelled by sale branch' });
      toast.success('Order cancelled');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setLoading(prev => ({ ...prev, [order.id]: false }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="outgoing-order-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 bg-amber-50 border-b border-amber-100">
          <h2 className="text-base font-semibold text-amber-900" data-testid="outgoing-dialog-title">
            My Active Pickups ({orders.length})
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-amber-100 transition-colors">
            <X size={18} className="text-amber-700" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
          {orders.map(order => (
            <div key={order.id} className="px-5 py-4" data-testid={`outgoing-order-${order.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-800 text-sm">{order.pickup_number}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(order.status)}`}>
                  {statusLabel(order.status)}
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Serving: <strong>{order.serving_branch_name}</strong></p>

              {/* Modified diff view */}
              {order.status === 'modified' && order.modifications?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                  <p className="text-xs font-semibold text-orange-800 mb-2">
                    Branch B proposed changes:
                  </p>
                  {order.modifications.flatMap(m => m.changes || []).map((ch, ci) => (
                    <div key={ci} className="text-xs text-orange-700 mb-1">
                      <span className="font-medium">{ch.product_name}</span>: {ch.field} changed from{' '}
                      <span className="bg-orange-100 px-1 rounded">{ch.old_value}</span> → {' '}
                      <span className="bg-emerald-100 px-1 rounded">{ch.new_value}</span>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleAcceptChanges(order)}
                      disabled={loading[order.id]}
                      className="px-3 py-1 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700 disabled:opacity-50"
                      data-testid={`btn-accept-changes-${order.id}`}
                    >
                      Accept Changes
                    </button>
                    <button
                      onClick={() => handleCancel(order)}
                      disabled={loading[order.id]}
                      className="px-3 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* OTP entry */}
              {order.status === 'otp_pending' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-emerald-800 mb-2">
                    Ask {order.serving_branch_name || 'Branch B'} for the 6-digit code, then enter it here to finalize:
                  </p>
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="000000"
                      value={otpInputs[order.id] || ''}
                      onChange={e => setOtpInputs(prev => ({ ...prev, [order.id]: e.target.value.replace(/\D/g, '') }))}
                      className="w-32 text-center font-mono text-lg border border-emerald-300 rounded px-2 py-1 tracking-widest focus:outline-none focus:border-emerald-500"
                      data-testid={`otp-input-${order.id}`}
                    />
                    <button
                      onClick={() => handleVerifyOtp(order)}
                      disabled={loading[order.id]}
                      className="px-3 py-1 text-sm font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`btn-verify-otp-${order.id}`}
                    >
                      {loading[order.id] ? '…' : 'Verify & Finalize'}
                    </button>
                    <button
                      onClick={() => handleCancel(order)}
                      disabled={loading[order.id]}
                      className="px-3 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Waiting for confirmation */}
              {order.status === 'pending_confirmation' && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-400 italic">Waiting for {order.serving_branch_name} to confirm…</p>
                  <button
                    onClick={() => handleCancel(order)}
                    disabled={loading[order.id]}
                    className="px-2 py-1 text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main exported component ─────────────────────────────────────────────────
export default function BranchPickupManager() {
  const { user, hasPerm, effectiveBranchId } = useAuth();
  const navigate = useNavigate();

  const [incomingCount, setIncomingCount]   = useState(0);
  const [incomingOrders, setIncomingOrders] = useState([]);
  const [outgoingOrders, setOutgoingOrders] = useState([]);

  const [showIncoming, setShowIncoming] = useState(false);
  const [showOutgoing, setShowOutgoing] = useState(false);
  const [selectedIncoming, setSelectedIncoming] = useState(null);
  // Remote print of a finalized pickup invoice — { inv, biz }
  const [remotePrint, setRemotePrint] = useState(null);

  const quickPollRef = useRef(null);
  const canView = user && hasPerm('branch_pickups', 'view');
  // Pass the active branch explicitly so pill correctness never depends on the
  // global axios interceptor. 'all' is sent as undefined (privileged org view).
  const activeBranch = effectiveBranchId && effectiveBranchId !== 'all' ? effectiveBranchId : undefined;

  const fetchIncoming = useCallback(async () => {
    if (!canView) return;
    try {
      const res = await api.get('/branch-pickups/incoming/count', { params: { branch_id: activeBranch } });
      setIncomingCount(res.data.count || 0);
      setIncomingOrders(res.data.orders || []);
    } catch { /* silent */ }
  }, [canView, activeBranch]);

  const fetchOutgoing = useCallback(async () => {
    if (!canView) return;
    try {
      const res = await api.get('/branch-pickups/outgoing/status', { params: { branch_id: activeBranch } });
      setOutgoingOrders(res.data.orders || []);
    } catch { /* silent */ }
  }, [canView, activeBranch]);

  const fetchAll = useCallback(() => {
    fetchIncoming();
    fetchOutgoing();
  }, [fetchIncoming, fetchOutgoing]);

  // Trigger a quick burst of polls after a user action
  const triggerQuickPoll = useCallback(() => {
    clearInterval(quickPollRef.current);
    let count = 0;
    quickPollRef.current = setInterval(() => {
      fetchAll();
      if (++count >= 3) clearInterval(quickPollRef.current);
    }, QUICK_POLL_MS);
  }, [fetchAll]);

  useEffect(() => {
    if (!canView) return;
    fetchAll();
    const interval = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearInterval(quickPollRef.current);
    };
  }, [canView, fetchAll]);

  const handleRefresh = () => {
    fetchAll();
    triggerQuickPoll();
  };

  // Same fetch as printPickupReceipt, but feeds the SendToPrintModal instead
  // of the local print dialog.
  const openRemotePrint = useCallback(async (invoiceId) => {
    try {
      const [invRes, bizRes] = await Promise.all([
        api.get(`/invoices/${invoiceId}`),
        api.get('/settings/business-info').catch(() => ({ data: {} })),
      ]);
      setRemotePrint({ inv: invRes.data || {}, biz: bizRes.data || {} });
    } catch {
      toast.error('Could not load invoice for remote print');
    }
  }, []);

  if (!canView) return null;

  const hasIncoming = incomingCount > 0;
  const hasOutgoing = outgoingOrders.length > 0;

  // Keep rendering while the remote-print modal is open — finalizing the last
  // pickup empties both lists, which must not unmount the modal.
  if (!hasIncoming && !hasOutgoing && !remotePrint) return null;

  return (
    <>
      {/* Floating pills — bottom right, above PriceScanManager (which uses bottom-4) */}
      <div
        className="fixed bottom-20 right-4 z-40 flex flex-col gap-2 items-end"
        data-testid="branch-pickup-manager"
      >
        {/* Amber pill — outgoing (Sale branch, Branch A) */}
        {hasOutgoing && (
          <button
            onClick={() => setShowOutgoing(true)}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full px-4 py-2 shadow-lg text-sm font-medium transition-all"
            data-testid="outgoing-pill"
          >
            <GitMerge size={15} />
            <span>{outgoingOrders.length} pickup{outgoingOrders.length !== 1 ? 's' : ''} active</span>
            {outgoingOrders.some(o => o.status === 'otp_pending') && (
              <span className="ml-1 bg-white text-amber-700 text-xs font-bold rounded-full px-1.5 py-0.5">
                OTP!
              </span>
            )}
          </button>
        )}

        {/* Indigo pill — incoming (Serving branch, Branch B) */}
        {hasIncoming && (
          <button
            onClick={() => setShowIncoming(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-4 py-2 shadow-lg text-sm font-medium transition-all"
            data-testid="incoming-pill"
          >
            <GitMerge size={15} />
            <span>{incomingCount} incoming pickup{incomingCount !== 1 ? 's' : ''}</span>
          </button>
        )}
      </div>

      {/* Incoming list dialog (select one to open confirm dialog) */}
      {showIncoming && !selectedIncoming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="incoming-list-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 bg-indigo-50 border-b border-indigo-100">
              <h2 className="font-semibold text-indigo-900">Incoming Pickups ({incomingCount})</h2>
              <button onClick={() => setShowIncoming(false)} className="p-1 rounded hover:bg-indigo-100">
                <X size={18} className="text-indigo-700" />
              </button>
            </div>
            <div className="divide-y max-h-80 overflow-y-auto">
              {incomingOrders.map(order => (
                <button
                  key={order.id}
                  className="w-full text-left px-5 py-3 hover:bg-indigo-50 transition-colors flex items-center justify-between"
                  onClick={() => { setSelectedIncoming(order); setShowIncoming(false); }}
                  data-testid={`incoming-order-row-${order.id}`}
                >
                  <div>
                    <div className="font-medium text-sm text-gray-800">{order.pickup_number}</div>
                    <div className="text-xs text-gray-500">{order.sale_branch_name} · {order.item_count} item{order.item_count !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(order.status)}`}>
                      {statusLabel(order.status)}
                    </span>
                    <Eye size={14} className="text-indigo-500" />
                  </div>
                </button>
              ))}
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                onClick={() => { navigate('/branch-pickups?tab=history'); setShowIncoming(false); }}
                className="text-xs text-indigo-600 hover:underline"
              >
                View all pickups
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual incoming order confirm/modify dialog */}
      {selectedIncoming && (
        <IncomingOrderDialog
          order={selectedIncoming}
          onClose={() => setSelectedIncoming(null)}
          onRefresh={handleRefresh}
        />
      )}

      {/* Outgoing orders dialog */}
      {showOutgoing && (
        <OutgoingOrderDialog
          orders={outgoingOrders}
          onClose={() => setShowOutgoing(false)}
          onRefresh={handleRefresh}
          onRemotePrint={openRemotePrint}
        />
      )}

      {/* Send to Branch Printer — finalized pickup invoice */}
      {remotePrint && (
        <SendToPrintModal
          open={!!remotePrint}
          onOpenChange={(o) => { if (!o) setRemotePrint(null); }}
          documentType="sales_receipt"
          receiverDocType="invoice"
          documentName={`Sales Receipt #${remotePrint.inv.invoice_number || ''}`}
          documentId={remotePrint.inv.id || ''}
          docCode={remotePrint.inv.doc_code || ''}
          referenceNumber={remotePrint.inv.invoice_number || ''}
          branchId={remotePrint.inv.branch_id || ''}
          buildHtml={(format) => PrintEngine.generateHtml({ type: PrintEngine.getDocType(remotePrint.inv) || 'order_slip', data: remotePrint.inv, format, businessInfo: remotePrint.biz, docCode: remotePrint.inv.doc_code || '' })}
          metadata={{ invoice_id: remotePrint.inv.id, customer_name: remotePrint.inv.customer_name }}
        />
      )}
    </>
  );
}
